import { useCallback, useState } from 'react';
import { checkToConnectionRequestDistribution } from '../../api/node/toConnectionRequest';

/**
 * Wraps a distribution submit with the incoherence check shared by the single
 * and bulk paths: ask the API first, run the submit straight away when nothing
 * is wrong, otherwise hold the payload until the DMC confirms and replay it
 * with `confirmed: true`.
 *
 * The API re-runs the same validation on write, so skipping this hook can never
 * distribute silently — it only costs the user a 409 instead of a popup.
 */
export function useDistributionGuard() {
  const [incoherences, setIncoherences] = useState([]);
  const [pending, setPending] = useState(null);

  const guard = useCallback(
    async ({ toConnectionRequestIds, request, submit }) => {
      let found = [];
      try {
        const response = await checkToConnectionRequestDistribution({
          toConnectionRequestIds,
          request,
        });
        found = response?.data ?? [];
      } catch {
        // The check is advisory: if it fails, go straight to the write, which
        // re-validates and answers 409 rather than distributing blindly.
        found = [];
      }

      if (found.length === 0) {
        return submit(request);
      }

      setIncoherences(found);
      setPending({ request, submit });
      return undefined;
    },
    [],
  );

  const cancel = useCallback(() => {
    setIncoherences([]);
    setPending(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!pending) return undefined;
    const { request, submit } = pending;
    setIncoherences([]);
    setPending(null);
    return submit({ ...request, confirmed: true });
  }, [pending]);

  return {
    incoherences,
    isOpen: incoherences.length > 0,
    guard,
    cancel,
    confirm,
  };
}
